import { useCallback } from 'react'

import { Wizard } from '@factorialco/deprecated-design-system'
import { useFormik } from 'formik'
import type { EmployeeForSelectFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { BasicInfoStep } from './steps/BasicInfoStep'
import { EditorsStep } from './steps/EditorsStep'
import { ParticipantsStep } from './steps/ParticipantsStep'
import type { SurveyFormData } from './types'

const DEFAULT_VALUES: SurveyFormData = {
  title: '',
  description: '',
  anonymous: false,
  restrictedViewersEnabled: false,
  editors: [],
  respondentStrategy: {
    strategy: 'all_employees',
    arguments: [],
  },
  endsAt: '',
}

interface Props {
  title: string
  initialValues?: SurveyFormData
  onClose: () => void
  onSubmit: (data: SurveyFormData) => void
}

export function SurveyWizard({ title, initialValues, onClose, onSubmit }: Props) {
  const { employee, access } = useCurrentSession()

  const form = useFormik<SurveyFormData>({
    initialValues: initialValues || DEFAULT_VALUES,
    onSubmit: (values) => {
      const currentUserIncluded = values.editors.some((editor) => editor.id === employee.id)
      const currentEmployeeForSelect: EmployeeForSelectFragment = {
        id: employee.id,
        fullName: access.fullName,
        firstName: employee.firstName,
        lastName: employee.lastName,
        access: {
          id: access.id,
          avatar: buildUrl(access.avatar),
        },
      }

      onSubmit({
        ...values,
        editors: currentUserIncluded
          ? values.editors
          : [...values.editors, currentEmployeeForSelect],
      })
    },
  })

  const formHandleSubmit = form.handleSubmit
  const handleSubmit = useCallback(() => {
    if (form.isSubmitting) return
    formHandleSubmit()
  }, [formHandleSubmit, form.isSubmitting])

  return (
    <Wizard.Modal
      size="large"
      title={title}
      icon="document-folder-board-edit"
      initialData={form.values}
      onSubmit={handleSubmit}
      onClose={onClose}
      onDataChanged={(newData) => form.setValues(newData)}
      steps={[
        {
          title: i18n.t('surveys.create_survey_wizard.steps.basic_info'),
          nextText: i18n.t('common.next'),
          render: (props) => <BasicInfoStep {...props} errors={form.errors} />,
          isCompleted: () => Boolean(form.values.title),
        },
        {
          title: i18n.t('surveys.create_survey_wizard.steps.editors_and_visibility'),
          nextText: i18n.t('common.next'),
          render: (props) => <EditorsStep {...props} errors={form.errors} />,
          isCompleted: () => true,
        },
        {
          title: i18n.t('surveys.create_survey_wizard.steps.participants_and_privacy'),
          nextText: i18n.t('common.submit'),
          render: (props) => <ParticipantsStep {...props} errors={form.errors} />,
          isCompleted: () => true,
        },
      ]}
    />
  )
}
